export class Bot {
  constructor(car) {
    this.car = car;
    this.controls = {
      forward: 0,
      backward: 0,
      left: 0,
      right: 0,
      jump: false,
      boost: false
    };
    
    // Improved bot behavior settings
    this.jumpCooldown = 0;
    this.minJumpCooldown = 60; // Better balanced cooldown
    this.jumpProbability = 0.3; // Increased to improve aerial capability
    this.boostProbability = 0.4; // More aggressive boost usage
    this.lastDecisionTime = 0;
    this.decisionInterval = 50; // Faster decision making (was 100ms)
    
    // New strategic positioning variables
    this.targetPosition = { x: 0, y: 0, z: 0 };
    this.state = 'chase'; // chase, defend, recover, aerial
    this.lastStateChange = 0;
    this.minStateDuration = 500; // Minimum time to stay in a state
    this.teamSide = this.car.team === 'blue' ? -1 : 1; // Team's side of field
    
    console.log(`Bot created for ${this.car.team} team`);
  }
  
  update(ball, gameTime) {
    // Only make decisions at certain intervals
    const currentTime = Date.now();
    if (currentTime - this.lastDecisionTime < this.decisionInterval) {
      return this.controls;
    }
    this.lastDecisionTime = currentTime;
    
    // Reset controls
    this.controls = {
      forward: 0,
      backward: 0,
      left: 0,
      right: 0,
      jump: false,
      boost: false
    };
    
    // Get positions
    const carPos = this.car.getPosition();
    const ballPos = ball.getPosition();
    const ballVelocity = ball.body ? ball.body.velocity : { x: 0, y: 0, z: 0 };
    
    // Update bot state based on game situation
    this.updateBotState(carPos, ballPos, ballVelocity, currentTime);
    
    // Calculate distances and angles
    const dx = ballPos.x - carPos.x;
    const dz = ballPos.z - carPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    const angle = Math.atan2(dx, dz);
    const carAngle = this.car.mesh.rotation.y;
    const angleDiff = this.normalizeAngle(angle - carAngle);
    
    // Handle behavior based on current state
    switch (this.state) {
      case 'chase':
        this.chaseBall(distance, angleDiff, ballPos, carPos);
        break;
        
      case 'defend':
        this.defendGoal(distance, angleDiff, ballPos, carPos);
        break;
        
      case 'recover':
        this.recoverPosition(distance, angleDiff, ballPos, carPos);
        break;
        
      case 'aerial':
        this.attemptAerial(distance, angleDiff, ballPos, carPos);
        break;
    }
    
    // Jump logic with cooldown
    if (this.jumpCooldown > 0) {
      this.jumpCooldown--;
    } else {
      // Jump if in aerial state or if ball is above and we're close
      if (this.state === 'aerial' || (distance < 5 && ballPos.y > 2)) {
        if (Math.random() < this.jumpProbability) {
          this.controls.jump = true;
          this.jumpCooldown = this.minJumpCooldown;
        }
      }
    }
    
    return this.controls;
  }
  
  updateBotState(carPos, ballPos, ballVelocity, currentTime) {
    // Don't change state too frequently
    if (currentTime - this.lastStateChange < this.minStateDuration) {
      return;
    }
    
    // Calculate distances
    const distanceToBall = Math.sqrt(
      Math.pow(ballPos.x - carPos.x, 2) + 
      Math.pow(ballPos.z - carPos.z, 2)
    );
    
    // Predict ball position 1 second in the future
    const predictedBallPos = {
      x: ballPos.x + ballVelocity.x * 1.0,
      y: ballPos.y + ballVelocity.y * 1.0,
      z: ballPos.z + ballVelocity.z * 1.0
    };
    
    // Goal positions (simplified)
    const ownGoal = { x: this.teamSide * 50, y: 0, z: 0 };
    const enemyGoal = { x: -this.teamSide * 50, y: 0, z: 0 };
    
    // Distance from ball to own goal
    const ballToOwnGoal = Math.sqrt(
      Math.pow(ballPos.x - ownGoal.x, 2) + 
      Math.pow(ballPos.z - ownGoal.z, 2)
    );
    
    // Choose state based on game situation
    let newState = 'chase'; // Default state
    
    // If ball is high, consider an aerial
    if (ballPos.y > 5 && distanceToBall < 10 && this.car.boostAmount > 30) {
      newState = 'aerial';
    }
    // If ball is coming toward our goal and we're not in good position
    else if (
      ballVelocity.x * this.teamSide > 0 && // Ball moving toward our goal
      ballToOwnGoal < 30 && // Ball near our goal
      distanceToBall > 10 // We're far from the ball
    ) {
      newState = 'defend';
    }
    // If we're out of position
    else if (
      (this.teamSide * carPos.x < -10) && // We're on the opponent's side
      (this.teamSide * ballPos.x > 10) // Ball is on our side
    ) {
      newState = 'recover';
    }
    // Otherwise chase the ball
    else {
      newState = 'chase';
    }
    
    // Set new state if different
    if (newState !== this.state) {
      this.state = newState;
      this.lastStateChange = currentTime;
      console.log(`Bot switched to ${this.state} state`);
    }
  }
  
  chaseBall(distance, angleDiff, ballPos, carPos) {
    // Move toward ball
    this.controls.forward = 1;
    
    // Turn toward ball
    if (angleDiff > 0.1) {
      this.controls.right = 0;
      this.controls.left = 1;
    } else if (angleDiff < -0.1) {
      this.controls.left = 0;
      this.controls.right = 1;
    }
    
    // Use boost when appropriate - far from ball and facing it
    if (distance > 10 && Math.abs(angleDiff) < 0.3 && this.car.boostAmount > 20) {
      this.controls.boost = Math.random() < this.boostProbability;
    }
  }
  
  defendGoal(distance, angleDiff, ballPos, carPos) {
    // Calculate a defensive position between ball and goal
    const ownGoal = { x: this.teamSide * 50, y: 0, z: 0 };
    
    // Position between ball and goal
    const targetX = ballPos.x + (ownGoal.x - ballPos.x) * 0.7;
    const targetZ = ballPos.z + (ownGoal.z - ballPos.z) * 0.7;
    
    // Calculate distance and angle to target position
    const dx = targetX - carPos.x;
    const dz = targetZ - carPos.z;
    const targetDistance = Math.sqrt(dx * dx + dz * dz);
    const targetAngle = Math.atan2(dx, dz);
    const targetAngleDiff = this.normalizeAngle(targetAngle - carPos.rotation?.y || 0);
    
    // Move toward target position
    this.controls.forward = 1;
    
    // Turn toward target
    if (targetAngleDiff > 0.1) {
      this.controls.right = 0;
      this.controls.left = 1;
    } else if (targetAngleDiff < -0.1) {
      this.controls.left = 0;
      this.controls.right = 1;
    }
    
    // Use boost to get back quickly if far from target
    if (targetDistance > 15 && Math.abs(targetAngleDiff) < 0.3 && this.car.boostAmount > 30) {
      this.controls.boost = true;
    }
  }
  
  recoverPosition(distance, angleDiff, ballPos, carPos) {
    // Calculate center field position to rotate back
    const centerX = this.teamSide * 20; // Defensive half of field
    const centerZ = 0;
    
    // Calculate distance and angle to center position
    const dx = centerX - carPos.x;
    const dz = centerZ - carPos.z;
    const centerDistance = Math.sqrt(dx * dx + dz * dz);
    const centerAngle = Math.atan2(dx, dz);
    const centerAngleDiff = this.normalizeAngle(centerAngle - carPos.rotation?.y || 0);
    
    // Move toward center
    this.controls.forward = 1;
    
    // Turn toward center
    if (centerAngleDiff > 0.1) {
      this.controls.right = 0;
      this.controls.left = 1;
    } else if (centerAngleDiff < -0.1) {
      this.controls.left = 0;
      this.controls.right = 1;
    }
    
    // Use boost to get back quickly if far from center
    if (centerDistance > 20 && Math.abs(centerAngleDiff) < 0.3 && this.car.boostAmount > 30) {
      this.controls.boost = true;
    }
  }
  
  attemptAerial(distance, angleDiff, ballPos, carPos) {
    // Only attempt aerial if we have enough boost
    if (this.car.boostAmount < 20) {
      this.state = 'chase'; // Revert to chase if not enough boost
      return;
    }
    
    // Position car under the ball
    const targetX = ballPos.x;
    const targetZ = ballPos.z;
    
    // Calculate distance and angle to position under ball
    const dx = targetX - carPos.x;
    const dz = targetZ - carPos.z;
    const targetDistance = Math.sqrt(dx * dx + dz * dz);
    const targetAngle = Math.atan2(dx, dz);
    const targetAngleDiff = this.normalizeAngle(targetAngle - carPos.rotation?.y || 0);
    
    // Move toward position under ball
    this.controls.forward = 1;
    
    // Turn toward position
    if (targetAngleDiff > 0.1) {
      this.controls.right = 0;
      this.controls.left = 1;
    } else if (targetAngleDiff < -0.1) {
      this.controls.left = 0;
      this.controls.right = 1;
    }
    
    // If close to position under ball and ball is high, jump and boost
    if (targetDistance < 2 && ballPos.y > 3) {
      this.controls.jump = true;
      this.jumpCooldown = 5; // Short cooldown for double jump
      this.controls.boost = true;
    }
  }
  
  normalizeAngle(angle) {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;
    return angle;
  }
} 